import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  LayoutDashboard, Building2, Users, Package, FileText, BarChart3,
  MessageSquare, Settings, HelpCircle, LogOut, Bell, Menu, X, 
  ChevronRight, Clock, AlertTriangle, CheckCircle, Heart, RefreshCw,
  Award, UserCog, Percent, Shield, AlertCircle
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { path: '/portal/dashboard', label: 'Início', icon: LayoutDashboard },
  { path: '/portal/instituicao', label: 'Minha Instituição', icon: Building2 },
  { path: '/portal/valencias', label: 'Utentes e Respostas Sociais', icon: Users },
  { path: '/portal/pedidos', label: 'Pedidos de Apoio', icon: Package },
  { path: '/portal/voluntariado', label: 'Voluntariado', icon: Heart },
  { path: '/portal/documentos', label: 'Documentos', icon: FileText },
  { path: '/portal/relatorios', label: 'Relatórios', icon: BarChart3 },
  { path: '/portal/mensagens', label: 'Mensagens', icon: MessageSquare },
  { path: '/portal/definicoes', label: 'Definições', icon: Settings },
  { path: '/portal/ajuda', label: 'Ajuda', icon: HelpCircle },
];

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificacoesCount, setNotificacoesCount] = useState(0);

  useEffect(() => {
    const checkAuth = () => {
      const storedToken = localStorage.getItem('portal_token');
      const storedUser = localStorage.getItem('portal_user');
      
      if (storedToken && storedUser) {
        try {
          setUser(JSON.parse(storedUser));
          setLoading(false);
          loadNotificacoesCount();
        } catch (e) {
          // Invalid user data, redirect to login
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_user');
          navigate('/portal/login', { replace: true });
        }
      } else {
        // No auth, redirect to login
        navigate('/portal/login', { replace: true });
      }
    };
    
    // Small delay to ensure localStorage is fully written after login
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  useEffect(() => {
    if (user) loadNotificacoesCount();
  }, [location.pathname]);

  const loadNotificacoesCount = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/notificacoes?lidas=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificacoesCount(data.length);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_refresh_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
    toast.success('Sessão terminada');
  };

  if (!user || loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="portal-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-slate-100 rounded-lg">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#0066CC] rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-sm">{user.instituicao_nome?.slice(0, 20)}</span>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-72 bg-white border-r z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between lg:justify-start gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0066CC] rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">ENTRAJUDATA</p>
                <p className="text-xs text-slate-500">Portal das Instituições</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Institution Info */}
        <div className="p-4 border-b bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#0066CC]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{user.instituicao_nome}</p>
              {user.instituicao_codigo_ea && (
                <Badge variant="outline" className="mt-1">{user.instituicao_codigo_ea}</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#0066CC] text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.path === '/portal/mensagens' && notificacoesCount > 0 && (
                    <Badge className="bg-red-500">{notificacoesCount}</Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium">{user.nome?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-900 truncate">{user.nome}</p>
              <p className="text-xs text-slate-500">{user.role === 'INST_ADMIN' ? 'Administrador' : 'Utilizador'}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 pt-16 lg:pt-0">
        {/* Desktop Header */}
        <div className="hidden lg:flex h-16 bg-white border-b items-center justify-between px-6">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              {NAV_ITEMS.find(item => item.path === location.pathname)?.label || 'Portal'}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="/portal/notificacoes" 
              className="p-2 hover:bg-slate-100 rounded-lg relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {notificacoesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {notificacoesCount}
                </span>
              )}
            </Link>
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">{user.nome?.charAt(0)}</span>
              </div>
              <span className="text-sm text-slate-600">{user.nome}</span>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// Dashboard Home Component
export function PortalDashboardHome() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [documentosAviso, setDocumentosAviso] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const [dashResponse, avisoResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos-aviso`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (dashResponse.ok) {
        const data = await dashResponse.json();
        setDashboard(data);
      } else if (dashResponse.status === 401) {
        navigate('/portal/login');
      }

      if (avisoResponse.ok) {
        const avisos = await avisoResponse.json();
        setDocumentosAviso(avisos || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Erro ao carregar dados</p>
        <Button onClick={loadDashboard} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Bem-vindo, {dashboard.instituicao.nome}
          </h1>
          <p className="text-slate-500">
            Último login: {dashboard.ultimo_login ? new Date(dashboard.ultimo_login).toLocaleString('pt-PT') : 'Primeiro acesso'}
          </p>
        </div>
        {dashboard.instituicao.codigo_ea && (
          <Badge className="bg-[#0066CC] text-white text-sm px-4 py-1">
            {dashboard.instituicao.codigo_ea}
          </Badge>
        )}
      </div>

      {/* Document Expiry Alert Banner */}
      {documentosAviso.length > 0 && (
        <div
          data-testid="documentos-aviso-banner"
          className={`border rounded-lg p-4 ${
            documentosAviso.some(d => d.urgencia === 'expirado')
              ? 'bg-red-50 border-red-200'
              : documentosAviso.some(d => d.urgencia === 'critico')
              ? 'bg-orange-50 border-orange-200'
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 mt-0.5 shrink-0 ${
              documentosAviso.some(d => d.urgencia === 'expirado') ? 'text-red-600'
              : documentosAviso.some(d => d.urgencia === 'critico') ? 'text-orange-600'
              : 'text-amber-600'
            }`} />
            <div className="flex-1">
              <p className={`font-semibold ${
                documentosAviso.some(d => d.urgencia === 'expirado') ? 'text-red-800'
                : documentosAviso.some(d => d.urgencia === 'critico') ? 'text-orange-800'
                : 'text-amber-800'
              }`}>
                {documentosAviso.filter(d => d.urgencia === 'expirado').length > 0
                  ? `${documentosAviso.filter(d => d.urgencia === 'expirado').length} documento(s) expirado(s)`
                  : documentosAviso.filter(d => d.urgencia === 'critico').length > 0
                  ? `${documentosAviso.filter(d => d.urgencia === 'critico').length} documento(s) a expirar em menos de 7 dias`
                  : `${documentosAviso.length} documento(s) a expirar nos próximos 30 dias`
                }
              </p>
              <ul className="mt-2 space-y-1">
                {documentosAviso.slice(0, 5).map((doc) => (
                  <li key={doc.id} className="flex items-start gap-2 text-sm">
                    <span className={`mt-1 shrink-0 ${
                      doc.urgencia === 'expirado' ? 'text-red-500'
                      : doc.urgencia === 'critico' ? 'text-orange-500'
                      : 'text-amber-500'
                    }`}>•</span>
                    <span className={
                      doc.urgencia === 'expirado' ? 'text-red-700'
                      : doc.urgencia === 'critico' ? 'text-orange-700'
                      : 'text-amber-700'
                    }>
                      <strong>{doc.nome}</strong>
                      {doc.urgencia === 'expirado'
                        ? ` — Expirado há ${Math.abs(doc.dias_restantes)} dia(s)`
                        : ` — Expira em ${doc.dias_restantes} dia(s) (${new Date(doc.data_limite).toLocaleDateString('pt-PT')})`
                      }
                      {doc.tipo_aviso === 'pendente' && (
                        <span className="ml-1 text-xs font-medium opacity-75">[pendente de entrega]</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/portal/documentos"
                className={`inline-flex items-center mt-3 text-sm font-medium ${
                  documentosAviso.some(d => d.urgencia === 'expirado') ? 'text-red-700 hover:text-red-900'
                  : documentosAviso.some(d => d.urgencia === 'critico') ? 'text-orange-700 hover:text-orange-900'
                  : 'text-amber-700 hover:text-amber-900'
                }`}
              >
                <FileText className="w-4 h-4 mr-1" />
                Ver todos os documentos
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns Alert Banner */}
      {dashboard.campanhas_pendentes > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-blue-800">
                {dashboard.campanhas_pendentes} Campanha(s) de Atualização Pendente(s)
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Existem campanhas de atualização de dados que aguardam a sua resposta.
              </p>
              <Link 
                to="/portal/atualizacao" 
                className="inline-flex items-center mt-2 text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                Responder às campanhas
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Alert Banner */}
      {dashboard.tarefas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Ações pendentes</p>
              <ul className="mt-1 space-y-1">
                {dashboard.tarefas.map((tarefa, i) => (
                  <li key={i} className="text-sm text-amber-700 flex items-center gap-2">
                    <span className={
                      tarefa.tipo === 'error' ? 'text-red-600' : 
                      tarefa.tipo === 'campanha' ? 'text-blue-600' : 'text-amber-600'
                    }>•</span>
                    {tarefa.acao_url ? (
                      <Link to={tarefa.acao_url} className="hover:underline">
                        {tarefa.titulo}
                      </Link>
                    ) : (
                      tarefa.titulo
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Utentes Card - NEW */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/valencias')} data-testid="card-utentes">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Utentes</p>
                <p className="text-2xl font-bold mt-1 text-[#0066CC]">
                  {dashboard.cards.utentes?.total?.toLocaleString('pt-PT') || dashboard.cards.valencias.total_utentes?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  pessoas apoiadas
                </p>
              </div>
              <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tecnicos Card - NEW */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/instituicao')} data-testid="card-tecnicos">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Técnicos</p>
                <p className="text-2xl font-bold mt-1 text-purple-600">
                  {dashboard.cards.tecnicos?.total || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  profissionais
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <UserCog className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Voluntarios Card - NEW */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/voluntariado')} data-testid="card-voluntarios">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Voluntários</p>
                <p className="text-2xl font-bold mt-1 text-pink-600">
                  {dashboard.cards.voluntarios?.total || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  a colaborar
                </p>
              </div>
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Valencias Card */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/valencias')} data-testid="card-valencias">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Respostas Sociais Ativas</p>
                <p className="text-2xl font-bold mt-1">{dashboard.cards.valencias.total}</p>
                {dashboard.cards.valencias.sem_demograficos > 0 && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs mt-1">
                    {dashboard.cards.valencias.sem_demograficos} sem dados demo.
                  </Badge>
                )}
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                dashboard.cards.valencias.sem_demograficos > 0 ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                <Building2 className={`w-5 h-5 ${
                  dashboard.cards.valencias.sem_demograficos > 0 ? 'text-amber-600' : 'text-blue-600'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Certificacao Card - UPDATED to match admin style */}
        <Card className={`hover:shadow-md transition-shadow ${
          dashboard.cards.certificacao?.certificada 
            ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200' 
            : ''
        }`} data-testid="card-certificacao">
          <CardContent className="pt-6">
            {dashboard.cards.certificacao?.certificada ? (
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-amber-800">Instituição Certificada EA</p>
                  <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs mt-1">
                    {dashboard.cards.certificacao.tipo || 'Certificada'}
                  </Badge>
                  {dashboard.cards.certificacao.data_validade && (
                    <p className="text-xs text-amber-600 mt-2">
                      Válido até: {new Date(dashboard.cards.certificacao.data_validade).toLocaleDateString('pt-PT')}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Certificação</p>
                  <p className="text-lg font-medium mt-1 text-slate-400">Não Certificada</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Solicite avaliação para certificação
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
                  <Award className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Qualidade Info Card - NEW */}
        <Card className="hover:shadow-md transition-shadow" data-testid="card-qualidade">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-500">Qualidade da Informação</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className={`text-2xl font-bold ${
                    (dashboard.cards.qualidade_info?.score || 0) >= 70 ? 'text-green-600' : 
                    (dashboard.cards.qualidade_info?.score || 0) >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {dashboard.cards.qualidade_info?.score || 0}%
                  </p>
                  <p className="text-xs text-slate-400">
                    {dashboard.cards.qualidade_info?.nivel || 'N/A'}
                  </p>
                </div>
                <Progress 
                  value={dashboard.cards.qualidade_info?.score || 0} 
                  className="h-2 mt-2"
                />
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ml-3 ${
                (dashboard.cards.qualidade_info?.score || 0) >= 70 ? 'bg-green-100' : 
                (dashboard.cards.qualidade_info?.score || 0) >= 50 ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                <Percent className={`w-5 h-5 ${
                  (dashboard.cards.qualidade_info?.score || 0) >= 70 ? 'text-green-600' : 
                  (dashboard.cards.qualidade_info?.score || 0) >= 50 ? 'text-amber-600' : 'text-red-600'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dados Card */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/instituicao')} data-testid="card-dados">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Dados Instituição</p>
                <p className="text-2xl font-bold mt-1">
                  {dashboard.cards.dados.status === 'atualizado' ? (
                    <span className="text-green-600">Atualizado</span>
                  ) : (
                    <span className="text-amber-600">Desatualizado</span>
                  )}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {dashboard.cards.dados.ultima_atualizacao 
                    ? new Date(dashboard.cards.dados.ultima_atualizacao).toLocaleDateString('pt-PT')
                    : '-'}
                </p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                dashboard.cards.dados.status === 'atualizado' ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {dashboard.cards.dados.status === 'atualizado' 
                  ? <CheckCircle className="w-5 h-5 text-green-600" />
                  : <Clock className="w-5 h-5 text-amber-600" />
                }
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documentos Card */}
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/portal/documentos')} data-testid="card-documentos">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Documentos</p>
                <p className="text-2xl font-bold mt-1">{dashboard.cards.documentos.total ?? dashboard.cards.documentos.validos}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {dashboard.cards.documentos.expirados > 0 && (
                    <Badge className="bg-red-500 text-[10px] px-1.5 py-0">
                      {dashboard.cards.documentos.expirados} expirado(s)
                    </Badge>
                  )}
                  {dashboard.cards.documentos.a_expirar > 0 && (
                    <Badge className="bg-amber-500 text-[10px] px-1.5 py-0">
                      {dashboard.cards.documentos.a_expirar} a expirar
                    </Badge>
                  )}
                  {dashboard.cards.documentos.pendentes > 0 && (
                    <Badge variant="outline" className="text-slate-600 border-slate-300 text-[10px] px-1.5 py-0">
                      {dashboard.cards.documentos.pendentes} pendente(s)
                    </Badge>
                  )}
                </div>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                dashboard.cards.documentos.expirados > 0 ? 'bg-red-100' : dashboard.cards.documentos.a_expirar > 0 ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                <FileText className={`w-5 h-5 ${
                  dashboard.cards.documentos.expirados > 0 ? 'text-red-600' : dashboard.cards.documentos.a_expirar > 0 ? 'text-amber-600' : 'text-green-600'
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link 
              to="/portal/instituicao" 
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-[#0066CC]" />
                <span>Atualizar dados da instituição</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </Link>
            <Link 
              to="/portal/pedidos/novo" 
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-purple-600" />
                <span>Submeter novo pedido de recursos</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </Link>
            <Link 
              to="/portal/documentos" 
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-600" />
                <span>Gerir documentos</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </Link>
            <Link 
              to="/portal/valencias" 
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-600" />
                <span>Atualizar respostas sociais e utentes</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </Link>
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Notificações Recentes</CardTitle>
            <Link to="/portal/notificacoes" className="text-sm text-[#0066CC] hover:underline">
              Ver todas
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard.notificacoes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Sem notificações recentes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard.notificacoes.map((notif, i) => (
                  <div key={i} className={`p-3 rounded-lg ${notif.lida ? 'bg-slate-50' : 'bg-blue-50 border border-blue-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${notif.lida ? 'bg-slate-300' : 'bg-blue-500'}`} />
                      <div>
                        <p className="font-medium text-sm">{notif.titulo}</p>
                        <p className="text-xs text-slate-500 mt-1">{notif.mensagem}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(notif.created_at).toLocaleDateString('pt-PT')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
